import type { Scene, ShadowGenerator } from '@babylonjs/core';
import { MeshBuilder, StandardMaterial, Color3 } from '@babylonjs/core';

export interface DungeonArenaOptions {
  center?: { x: number; z: number };
  radiusMeters?: number;
}

export class DungeonArena {
  public readonly center: { x: number; z: number };
  public readonly radiusMeters: number;

  constructor(options: DungeonArenaOptions = {}) {
    this.center = options.center ?? { x: 0, z: 24.0 };
    this.radiusMeters = options.radiusMeters ?? 18.0;
  }

  public isInsideArena(pos: { x: number; z: number }): boolean {
    const dx = pos.x - this.center.x;
    const dz = pos.z - this.center.z;
    return Math.hypot(dx, dz) <= this.radiusMeters;
  }

  public getBossSpawnPosition(): { x: number; y: number; z: number } {
    return {
      x: this.center.x,
      y: 0,
      z: this.center.z + 5.0
    };
  }

  public spawnArenaEnvironment(scene: Scene, shadowGenerator?: ShadowGenerator): void {
    const pillarCount = 8;
    const pillarMat = new StandardMaterial('pillarMat', scene);
    pillarMat.diffuseColor = new Color3(0.12, 0.15, 0.22);
    pillarMat.specularColor = new Color3(0.3, 0.4, 0.6);

    const runeMat = new StandardMaterial('runeMat', scene);
    runeMat.diffuseColor = new Color3(0.2, 0.8, 1.0);
    runeMat.emissiveColor = new Color3(0.15, 0.5, 0.8);

    for (let i = 0; i < pillarCount; i++) {
      const angle = (i / pillarCount) * Math.PI * 2;
      const px = this.center.x + Math.cos(angle) * this.radiusMeters;
      const pz = this.center.z + Math.sin(angle) * this.radiusMeters;

      const pillar = MeshBuilder.CreateCylinder(
        `arena_pillar_${i}`,
        { height: 5.0, diameter: 1.2 },
        scene
      );
      pillar.position.set(px, 2.5, pz);
      pillar.material = pillarMat;
      pillar.checkCollisions = true;

      const rune = MeshBuilder.CreateTorus(
        `arena_rune_${i}`,
        { diameter: 1.5, thickness: 0.15, tessellation: 12 },
        scene
      );
      rune.position.set(px, 3.5, pz);
      rune.material = runeMat;

      if (shadowGenerator) {
        shadowGenerator.addShadowCaster(pillar);
      }
    }
  }
}
